import { useMemo, useState } from "react";
import { useAuth } from "../../auth/Authprovider";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  UserPlus,
  CheckCircle,
  XCircle,
  Eye,
  MoreHorizontal,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Key,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Search,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  TanStackDataTable,
  userTableConfig,
  AdminAddUser,
  AdminEditUser,
  UserBulkActions,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Input,
} from "@b2b/ui-components";
import { SellerStatus } from "@b2b/services";
import { useUserList } from "../../hooks/useUserList";
import { useUserActions } from "../../hooks/useUserActions";
import { PendingUser, UserFilters } from "../../types/user.types";
import { createUserColumns } from "./UserList.columns";

const UserList = () => {
  console.log("📋 UserList component rendered");
  const navigate = useNavigate();
  const { allowedModules } = useAuth();
  const canManageStaff = allowedModules === undefined;
  const [searchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(!!editParam);
  const [editingUserId, setEditingUserId] = useState<string | null>(
    editParam || null
  );
  const [activeTab, setActiveTab] = useState("all");
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);

  const normalizeSearchInput = (value: string) =>
    value
      .trim()
      .replace(/^["']+|["']+$/g, "")
      .replace(/\s+/g, " ");

  // Memoize filters to prevent unnecessary re-renders
  const advancedFilters = useMemo<UserFilters>(
    () => ({
      role: selectedRole && selectedRole !== "all" ? selectedRole : undefined,
    }),
    [selectedRole]
  );

  const {
    users,
    loading,
    error,
    pagination,
    setPagination,
    refresh,
    fetchUsers,
  } = useUserList({
    activeTab,
    autoFetch: true,
    advancedFilters,
    searchTerm,
  });

  const {
    loading: actionsLoading,
    bulkLoading,
    approveUser,
    rejectUser,
    activateUser,
    suspendUser,
    deleteUser,
    bulkApprove,
    bulkReject,
    bulkActivate,
    bulkSuspend,
    bulkDelete,
  } = useUserActions({
    onSuccess: () => {
      refresh();
      setSelectedUsers(new Set());
    },
  });

  const showToast = (type: "success" | "error" | "info", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  // Handle export
  const handleExport = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      const queryParams: any = {
        page: 1,
        limit: 100, // backend max limit
      };

      if (activeTab === "pending") {
        queryParams.isActive = false;
        queryParams.approvalStatus = "pending";
      } else if (activeTab === "approved") {
        queryParams.isActive = true;
        queryParams.approvalStatus = "approved";
      } else if (activeTab === "rejected") {
        queryParams.isActive = false;
        queryParams.approvalStatus = "rejected";
      }

      const { adminService } = await import("@b2b/services");

      if (searchTerm) {
        queryParams.search = searchTerm;
      }

      if (selectedRole && selectedRole !== "all") {
        queryParams.role = selectedRole;
      }

      const firstPageResult = await adminService.getAllUsers(queryParams);
      const exportUsers: PendingUser[] = Array.isArray(firstPageResult?.data)
        ? [...firstPageResult.data]
        : [];

      const totalPages = firstPageResult?.pagination?.totalPages ?? 1;

      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const pageResult = await adminService.getAllUsers({
            ...queryParams,
            page,
          });
          if (Array.isArray(pageResult?.data)) {
            exportUsers.push(...pageResult.data);
          }
        }
      }

      if (exportUsers.length === 0) {
        showToast("info", "No users available to export");
        return;
      }

      const csvEscape = (value: unknown): string => {
        const normalized = value == null ? "" : String(value);
        if (
          normalized.includes(",") ||
          normalized.includes('"') ||
          normalized.includes("\n")
        ) {
          return `"${normalized.replace(/"/g, '""')}"`;
        }
        return normalized;
      };

      const getExportStatus = (user: PendingUser): string => {
        if (user.role === "SELLER" || user.role === "BUYER_SELLER") {
          if (user.sellerStatus === SellerStatus.APPROVED) return "Approved";
          if (user.sellerStatus === SellerStatus.REJECTED) return "Rejected";
          if (user.sellerStatus === SellerStatus.SUSPENDED) return "Suspended";
          return "Pending Approval";
        }

        if (user.isActive) return "Approved";
        if (user.company?.approvalStatus === "rejected") return "Rejected";
        return "Pending";
      };

      const headers = [
        "User",
        "Email",
        "Type",
        "Status",
        "Join Date",
        "Company",
      ];

      const rows = exportUsers.map((user: PendingUser) => {
        const userName =
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.email;

        return [
          csvEscape(userName),
          csvEscape(user.email),
          csvEscape(user.role.toLowerCase().replaceAll("_", " ")),
          csvEscape(getExportStatus(user)),
          csvEscape(new Date(user.createdAt).toLocaleDateString()),
          csvEscape(user.company?.name || "N/A"),
        ].join(",");
      });

      const csvContent = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([`\uFEFF${csvContent}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_management_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast("success", "Users exported successfully");
    } catch (error) {
      console.error("Error exporting users:", error);
      showToast("error", "Failed to export users");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    setSelectedUsers((prev) => {
      const pageUserIds = users.map((u) => u.id);
      const areAllCurrentPageUsersSelected =
        pageUserIds.length > 0 && pageUserIds.every((id) => prev.has(id));
      const next = new Set(prev);

      if (areAllCurrentPageUsersSelected) {
        // Unselect only current page users, keep selections from other pages
        pageUserIds.forEach((id) => next.delete(id));
      } else {
        // Select all users visible on the current page
        pageUserIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const handleViewProfile = (userId: string) => {
    navigate(`/admin/userprofile/${userId}`);
  };

  const handleEditUser = (user: PendingUser) => {
    // Keep edit flow inside admin context and load role-specific form from selected user
    navigate(`/admin/profile_settings?userId=${user.id}&edit=true`);
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.size === 0) return;
    const selectedUsersArray = Array.from(selectedUsers);
    const selectedUsersData = users.filter((u) =>
      selectedUsersArray.includes(u.id)
    );
    const success = await bulkApprove(selectedUsersArray, selectedUsersData);
    if (success)
      showToast("success", `${selectedUsers.size} users approved successfully`);
  };

  const handleBulkReject = async () => {
    if (selectedUsers.size === 0) return;
    const selectedUsersArray = Array.from(selectedUsers);
    const selectedUsersData = users.filter((u) =>
      selectedUsersArray.includes(u.id)
    );
    const success = await bulkReject(selectedUsersArray, selectedUsersData);
    if (success)
      showToast("success", `${selectedUsers.size} users rejected successfully`);
  };

  const handleBulkActivate = async () => {
    if (selectedUsers.size === 0) return;
    const selectedUsersArray = Array.from(selectedUsers);
    const success = await bulkActivate(selectedUsersArray);
    if (success)
      showToast(
        "success",
        `${selectedUsers.size} users activated successfully`
      );
  };

  const handleBulkSuspend = async () => {
    if (selectedUsers.size === 0) return;
    const selectedUsersArray = Array.from(selectedUsers);
    const success = await bulkSuspend(selectedUsersArray);
    if (success)
      showToast(
        "success",
        `${selectedUsers.size} users suspended successfully`
      );
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    const selectedUsersArray = Array.from(selectedUsers);
    const success = await bulkDelete(selectedUsersArray);
    if (success)
      showToast("success", `${selectedUsers.size} users deleted successfully`);
  };

  const handleResetPassword = async (userId: string) => {
    if (
      !confirm(
        "Reset password for this user? A new password will be generated."
      )
    )
      return;
    try {
      const { adminService } = await import("@b2b/services");
      const result = await adminService.resetUserPassword(userId, {
        sendEmail: false,
      });
      if (result.data?.newPassword) {
        alert(
          `New password: ${result.data.newPassword}\n\nPlease copy this password and share it with the user.`
        );
        showToast("success", "Password reset successfully");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      showToast("error", "Failed to reset password");
    }
  };

  const handleApproveUser = async (userId: string, user: PendingUser) => {
    const success = await approveUser(userId, user);
    if (success) {
      showToast("success", "User approved successfully");
    } else {
      showToast("error", "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId: string, user: PendingUser) => {
    const success = await rejectUser(userId, user);
    if (success) {
      showToast("success", "User rejected successfully");
    } else {
      showToast("error", "Failed to reject user");
    }
  };

  const handleActivateUser = async (userId: string) => {
    const success = await activateUser(userId);
    if (success) {
      showToast("success", "User activated successfully");
    } else {
      showToast("error", "Failed to activate user");
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const success = await suspendUser(userId);
    if (success) {
      showToast("success", "User suspended successfully");
    } else {
      showToast("error", "Failed to suspend user");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const success = await deleteUser(userId);
    if (success) {
      showToast("success", "User deleted successfully");
    } else {
      showToast("error", "Failed to delete user");
    }
  };

  const handleRefreshUsers = () => {
    setSelectedUsers(new Set());
    refresh();
  };

  // Create column definitions with memoization
  const columns = useMemo(
    () =>
      createUserColumns({
        loading,
        actionsLoading: actionsLoading ?? false,
        selectedUsers,
        allUsers: users,
        onToggleSelection: toggleUserSelection,
        onToggleSelectAll: toggleSelectAll,
        onViewProfile: handleViewProfile,
        onEditUser: handleEditUser,
        onApproveUser: handleApproveUser,
        onRejectUser: handleRejectUser,
        onActivateUser: handleActivateUser,
        onSuspendUser: handleSuspendUser,
        onResetPassword: handleResetPassword,
        onDeleteUser: handleDeleteUser,
        showSelectColumn: true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, selectedUsers, loading, actionsLoading]
  );

  return (
    <div className="space-y-8 mt-5">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-blue-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : toast.type === "error" ? (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            Users Management
          </h1>
          <p className="text-muted-foreground">
            Manage buyers, sellers, and user permissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={loading || isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
          <Button
            variant="outline"
            onClick={handleRefreshUsers}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddUserModal(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <UserBulkActions
        selectedCount={selectedUsers.size}
        onBulkApprove={handleBulkApprove}
        onBulkReject={handleBulkReject}
        onBulkDelete={handleBulkDelete}
        onBulkActivate={handleBulkActivate}
        onBulkSuspend={handleBulkSuspend}
        onClearSelection={() => setSelectedUsers(new Set())}
        isLoading={bulkLoading}
      />

      {/* Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Users Management</CardTitle>
            <div className="flex items-center gap-4">
              {/* Filter Section */}
              <div className="flex items-center gap-2">
                <Label htmlFor="role-filter" className="text-sm font-medium">
                  Filter by Role:
                </Label>
                <Select
                  value={selectedRole}
                  onValueChange={(value) => setSelectedRole(value)}
                >
                  <SelectTrigger id="role-filter" className="w-[180px]">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem
                      value="all"
                      className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                    >
                      All Roles
                    </SelectItem>
                    <SelectItem
                      value="SUPER_ADMIN"
                      className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                    >
                      Super Admin
                    </SelectItem>
                    {canManageStaff && (
                    <SelectItem
                      value="STAFF"
                      className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                    >
                      Staff
                    </SelectItem>
                    )}
                    <SelectItem
                      value="BUYER"
                      className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                    >
                      Buyer
                    </SelectItem>
                    <SelectItem
                      value="SELLER"
                      className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                    >
                      Seller
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Search Section */}
              <div className="flex items-center gap-2">
                <Input
                  id="search-input"
                  type="text"
                  placeholder="Search users..."
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchTerm(normalizeSearchInput(searchInputValue));
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }
                  }}
                  className="w-[250px]"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm(normalizeSearchInput(searchInputValue));
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  disabled={loading}
                >
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchInputValue("");
                    setSearchTerm("");
                    setSelectedRole("all");
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  disabled={loading}
                  title="Clear search and filters"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
              <span className="text-sm text-muted-foreground">
                Total: {pagination.total}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            defaultValue="all"
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <TabsList className="grid w-full grid-cols-4 bg-gray-100 rounded-lg p-1">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
              >
                All Users
              </TabsTrigger>
              <TabsTrigger
                value="pending"
                className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
              >
                Pending
              </TabsTrigger>
              <TabsTrigger
                value="approved"
                className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
              >
                Approved
              </TabsTrigger>
              <TabsTrigger
                value="rejected"
                className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
              >
                Rejected
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading users...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <TanStackDataTable
                    data={users}
                    columns={columns}
                    isLoading={loading}
                    serverPagination={
                      pagination.totalPages > 1 &&
                      pagination.total > pagination.limit
                        ? {
                            pageIndex: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            totalPages: pagination.totalPages,
                            onPageChange: (page: number) => {
                              // Update pagination state first
                              setPagination((prev) => ({ ...prev, page }));
                              // Directly fetch the new page - this ensures immediate API call
                              fetchUsers(page, false);
                            },
                          }
                        : undefined
                    }
                    emptyMessage="No users found."
                    styles={{
                      headerBg: userTableConfig.styles?.headerBg,
                      headerText: userTableConfig.styles?.headerText,
                      rowHover: userTableConfig.styles?.rowHover,
                    }}
                    config={{
                      enableSorting: true,
                      enablePagination: false, // Server-side pagination
                      enableZebraStriping: false,
                    }}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AdminAddUser
        open={showAddUserModal}
        onOpenChange={setShowAddUserModal}
        canManageStaff={canManageStaff}
        onUserCreated={() => {
          refresh();
        }}
      />
      <AdminEditUser
        open={showEditUserModal}
        onOpenChange={(open) => {
          setShowEditUserModal(open);
          if (!open) setEditingUserId(null);
        }}
        canManageStaff={canManageStaff}
        userId={editingUserId}
        onUserUpdated={() => {
          refresh();
        }}
      />
    </div>
  );
};

export default UserList;
